import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import { useAdminWebSocketStore, MESSAGE_TYPES } from "../../stores/websocketStore";
import Chart from "chart.js/auto";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import GroupIcon from "@material-ui/icons/Group";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import StatCard from "./StatCard";
import DnsIcon from "@material-ui/icons/Dns";
import MeetingRoomIcon from "@material-ui/icons/MeetingRoom";
import moment from "moment";
import SportsEsportsIcon from "@material-ui/icons/SportsEsports";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import TimerIcon from "@material-ui/icons/Timer";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import { monitoringCharts } from "./monitoringChartsRegistry";
import PeriodLabelsSelectorChart from "./PeriodLabelsSelectorChart";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
        boxSizing: "border-box",
    },
    headerRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "1rem",
        flexWrap: "wrap",
        marginBottom: "1rem",
    },
    sectionTitle: {
        fontSize: "1.4rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
        marginBottom: 0,
    },
    containerCards: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        boxSizing: "border-box",
        overflow: "hidden",
        width: "100%",
        gap: "2rem",
    },
    containerQueueAnalitycs: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        boxSizing: "border-box",
        background: "transparent",
        width: "100%",
        marginTop: "2rem",
    },
    containerMMOverview: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        boxSizing: "border-box",
        background: "transparent",
        width: "100%",
        margin: "2rem 0",
    },
    containerChartLayout: {
        display: "grid",
        gridTemplateColumns: "1fr 26rem",
        width: "100%",
        marginTop: "2rem",
        gap: "2rem",
    },
    containerQueueBarChartsLayout: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        width: "100%",
        marginTop: "2rem",
        gap: "2rem",
    },
    containerChart: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: "100%",
        boxSizing: "border-box",
        borderRadius: 12,
        boxShadow: theme.palette.type === "dark" 
            ? "0 4px 20px rgba(0, 0, 0, 0.4)" 
            : "0 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "1rem",
        background: theme.palette.background.paper,
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.12)"
            : "1px solid #ced4da",
    },
    containerChartTitle: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        marginBottom: "0.5rem",
    },
    title: {
        fontSize: "1.2rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
    },
    containerSelectLabels: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center",
        gap: "1rem",
    },
    chartCanvasContainer: {
        position: "relative",
        height: "300px",
        width: "100%",
    },
    doughnutContainer: {
        height: "200px",
    },
    containerNewPlayers: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        boxSizing: "border-box",
        borderRadius: 12,
        boxShadow: theme.palette.type === "dark" 
            ? "0 4px 20px rgba(0, 0, 0, 0.4)" 
            : "0 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "1rem",
        background: theme.palette.background.paper,
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.12)"
            : "1px solid #ced4da",
    },
    containerNewPlayersList: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        width: "100%",
        gap: "0.75rem",
        boxSizing: "border-box",
        overflow: "hidden",
        marginTop: "0.5rem",
    },
    newPlayerItem: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "0.5rem 0.85rem",
        boxSizing: "border-box",
        maxWidth: "100%",
        overflow: "hidden",
        gap: "0.5rem",
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid #e5e7eb",
        borderRadius: 10,
        backgroundColor: theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.03)"
            : "#ffffff",
        transition: "background-color 150ms ease-in-out, border-color 150ms ease-in-out, box-shadow 150ms ease-in-out",
        "&:hover": {
            backgroundColor: theme.palette.type === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "#f8fafc",
            borderColor: theme.palette.type === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "#cbd5e1",
            boxShadow: theme.palette.type === "dark"
                ? "0 2px 10px rgba(0,0,0,0.3)"
                : "0 2px 10px rgba(0,0,0,0.06)",
        },
    },
    newPlayerPicture: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        objectFit: "cover",
        border: theme.palette.type === "dark"
            ? "1px solid rgba(255, 255, 255, 0.1)"
            : "1px solid #e5e7eb",
        backgroundColor: theme.palette.type === "dark"
            ? "rgba(255, 255, 255, 0.05)"
            : "#f3f4f6",
        flexShrink: 0,
    },
    newPlayerInfoContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "0.25rem",
        flex: 1,
        minWidth: 0,
        marginLeft: "0.75rem",
    },
    newPlayerNickname: {
        fontSize: "0.95rem",
        fontWeight: 500,
        color: theme.palette.text.primary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
    },
    newPlayerCountryContainer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "0.5rem",
        flexShrink: 0,
    },
    newPlayerCountryFlag: {
        width: 20,
        height: 14,
        borderRadius: 3,
        objectFit: "cover",
        backgroundColor: "transparent",
    },
    newPlayerJoinedDate: {
        fontSize: "0.85rem",
        color: theme.palette.text.secondary,
    },
}));

const systemHealthCardsData = [
    {
        id: "number-of-servers",
        title: "Servers Alive",
        value: 0,
        icon: <DnsIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of servers alive",
    },
];

const queueCardsData = [
    {
        id: "online-players",
        title: "Online Players",
        value: 0,
        icon: <GroupIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of players online now",
    },
    {
        id: "players-in-queue",
        title: "Players in Queue",
        value: 0,
        icon: <AccessTimeIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of players in queue",
    },
    {
        id: "average-queue-duration",
        title: "AVG Queue Duration",
        value: 0,
        icon: <TimerIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "average queue duration",
    },
    {
        id: "active-lobbies",
        title: "Active Lobbies",
        value: 0,
        icon: <MeetingRoomIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of active lobbies",
    },
];

const mmActivityStatCardsData = [
    {
        id: "matches-live",
        title: "Matches Live",
        value: 0,
        icon: <AutorenewIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of matches live",
    },
    {
        id: "matches-created-today",
        title: "Matches Created",
        value: 0,
        icon: <SportsEsportsIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of matches created today",
    },
    {
        id: "matches-completed",
        title: "Matches Completed",
        value: 0,
        icon: <CheckCircleIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "number of matches completed today",
    },
    {
        id: "average-match-duration",
        title: "AVG Match Duration",
        value: 0,
        icon: <TimerIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "average match duration",
    },
    {
        id: "completion-rate",
        title: "Completion Rate",
        value: 0,
        icon: <TrendingUpIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "completion rate",
    },
    {
        id: "held-groups-now",
        title: "Held Groups Now",
        value: 0,
        icon: <AccessTimeIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "compatible groups currently waiting for assignable server capacity",
    },
    {
        id: "blocked-players-now",
        title: "Blocked Players Now",
        value: 0,
        icon: <GroupIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "players currently sitting inside held groups",
    },
    {
        id: "blocked-groups-last-tick",
        title: "Blocked Groups Last Tick",
        value: 0,
        icon: <AutorenewIcon style={{ width: "100%", height: "100%", color: "#ffffff" }}/>,
        tooltipText: "groups blocked by server capacity during the most recent matchmaking tick",
    },
];

export default function MonitoringSection({ 
    user, 
    mmActivitySummary,
    queueAnalitycsSummary,
    newestPlayers,
    getDaysDataForMatchesChart,
    getDaysDataForQueueChart,
    sevenDaysMatchmakingDataForChart,
    thirtyDaysMatchmakingDataForChart,
    ninetyDaysMatchmakingDataForChart,
    sevenDaysQueueDataForChart,
    thirtyDaysQueueDataForChart,
    ninetyDaysQueueDataForChart,
    weeklyDataForChart,
    monthlyDataForChart,
    yearlyDataForChart,
    getMonthlyDataForChart,
    getYearlyDataForChart,
    matchDurationData = {},
    playersQueuedPerDayData = [],
    queueEfficiencyPerDayData = [],
    queueOutcomeDistributionData = [],
    matchmakingCapacityTrendData = [],
    getMatchmakingCapacityTrendData,
    serversAliveCount = 0,
}) {
    const classes = useStyles();
    const theme = useTheme();
    const { subscribe } = useAdminWebSocketStore();
    const [newPlayersData, setNewPlayersData] = useState({
        labels: [],
        data: []
    });
    const [matchesData, setMatchesData] = useState({
        labels: [],
        matchesCreated: [],
        matchesCancelled: []
    });
    const [queueData, setQueueData] = useState([]);
    const [queueDurationData, setQueueDurationData] = useState("seven_days");
    const [matchesChartPeriod, setMatchesChartPeriod] = useState("seven_days");
    const [matchmakingCapacityPeriod, setMatchmakingCapacityPeriod] = useState("seven_days");
    const [signupsChartPeriod, setSignupsChartPeriod] = useState("week");

    const formatDateLabel = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString + "T00:00:00");
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const month = months[date.getMonth()];
        const day = date.getDate();
        return `${month} ${day}`;
    };

    const formatMonthLabel = (dateString) => {
        if (!dateString) return "";
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        if (/^[a-zA-Z]{3}$/.test(dateString)) {
            const s = dateString.toLowerCase();
            return s.charAt(0).toUpperCase() + s.slice(1);
        }
        const parts = dateString.split("-");
        if (parts.length >= 2) {
            const monthIndex = parseInt(parts[1], 10) - 1;
            if (!isNaN(monthIndex) && monthIndex >= 0 && monthIndex < 12) {
                return months[monthIndex];
            }
        }
        try {
            const d = new Date(dateString + "-01T00:00:00");
            const mi = d.getMonth();
            if (!isNaN(mi)) return months[mi];
        } catch (e) {}
        return "";
    };

    const selectPeriodData = (periodKey, periodMap) => {
        const value = periodMap[periodKey];
        return Array.isArray(value) && value.length ? value : [];
    };

    useEffect(() => {
        const dataMap = {
            week: weeklyDataForChart,
            month: monthlyDataForChart,
            year: yearlyDataForChart,
        };

        const dataToUse = selectPeriodData(signupsChartPeriod, dataMap);
        const labels = dataToUse.map(item => (
            signupsChartPeriod === "year"
                ? formatMonthLabel(item.date)
                : formatDateLabel(item.date)
        ));
        setNewPlayersData({ labels, data: dataToUse.map(item => item.value) });
    }, [signupsChartPeriod, weeklyDataForChart, monthlyDataForChart, yearlyDataForChart]);

    useEffect(() => {
        const dataMap = {
            seven_days: sevenDaysMatchmakingDataForChart,
            thirty_days: thirtyDaysMatchmakingDataForChart,
            ninety_days: ninetyDaysMatchmakingDataForChart,
        };

        const dataToUse = selectPeriodData(matchesChartPeriod, dataMap);
        setMatchesData({
            labels: dataToUse.map(item => formatDateLabel(item.date)),
            matchesCreated: dataToUse.map(item => item.matchesCreated || 0),
            matchesCancelled: dataToUse.map(item => item.matchesCancelled || 0)
        });
    }, [matchesChartPeriod, sevenDaysMatchmakingDataForChart, thirtyDaysMatchmakingDataForChart, ninetyDaysMatchmakingDataForChart]);

    useEffect(() => {
        const dataMap = {
            seven_days: sevenDaysQueueDataForChart,
            thirty_days: thirtyDaysQueueDataForChart,
            ninety_days: ninetyDaysQueueDataForChart,
        };

        const dataToUse = selectPeriodData(queueDurationData, dataMap);
        setQueueData(dataToUse);
    }, [queueDurationData, sevenDaysQueueDataForChart, thirtyDaysQueueDataForChart, ninetyDaysQueueDataForChart]);

    useEffect(() => {
        const instances = [];
        const isDark = theme.palette.type === "dark";
        monitoringCharts.forEach((chartDef) => {
            const { ref, configFactory, getData } = chartDef;
            if (!ref.current) return;
            const ctx = ref.current.getContext("2d");
            if (!ctx) return;
            const data = getData({ 
                newPlayersData, 
                matchesData, 
                queueData, 
                matchDurationData: matchDurationData || {},
                playersQueuedPerDayData,
                queueEfficiencyPerDayData,
                queueOutcomeDistributionData,
                matchmakingCapacityTrendData,
            });
            const config = configFactory(data, isDark);
            const instance = new Chart(ctx, config);
            instances.push({ id: chartDef.id, instance });
        });
        return () => {
            instances.forEach((entry) => entry.instance?.destroy());
        };
    }, [newPlayersData, matchesData, queueData, matchDurationData, playersQueuedPerDayData, queueEfficiencyPerDayData, queueOutcomeDistributionData, matchmakingCapacityTrendData, theme.palette.type]);

    useEffect(() => {
        const unsubscribe = subscribe(({ message }) => {
            if (!message) return;

            const eventAsJson = message;
            console.log("Received admin WebSocket message:", eventAsJson);

            switch (eventAsJson.messageId) {
                case MESSAGE_TYPES.INIT_HANDSHAKE:
                case MESSAGE_TYPES.HANDSHAKE_COMPLETED:
                    // Expected control messages during connect/handshake
                    break;

                case MESSAGE_TYPES.ONLINE_USER_COUNT:
                    console.log("Received online user count:", eventAsJson.count);
                    break;

                case MESSAGE_TYPES.USER_STATUS_CHANGED:
                    console.log("User status changed, new count:", eventAsJson.onlineUserCount);
                    break;

                default:
                    console.log("Unhandled admin message type:", eventAsJson.messageId);
            }
        });

        return unsubscribe;
    }, [subscribe]);

    const getSevenDaysDataForMatchmakingChart = () => {
        setMatchesChartPeriod("seven_days");
    };

    const getThirtyDaysDataForMatchmakingChart = () => {
        setMatchesChartPeriod("thirty_days");
        getDaysDataForMatchesChart(30);
    };

    const getNinetyDaysDataForMatchmakingChart = () => {
        setMatchesChartPeriod("ninety_days");
        getDaysDataForMatchesChart(90);
    };

    const getSevenDaysDataForMatchmakingCapacityChart = () => {
        setMatchmakingCapacityPeriod("seven_days");
        getMatchmakingCapacityTrendData(7);
    };

    const getThirtyDaysDataForMatchmakingCapacityChart = () => {
        setMatchmakingCapacityPeriod("thirty_days");
        getMatchmakingCapacityTrendData(30);
    };

    const getNinetyDaysDataForMatchmakingCapacityChart = () => {
        setMatchmakingCapacityPeriod("ninety_days");
        getMatchmakingCapacityTrendData(90);
    };

    const getSevenDaysDataForQueueChart = () => {
        setQueueDurationData("seven_days");
    };

    const getThirtyDaysDataForQueueChart = () => {
        setQueueDurationData("thirty_days");
        getDaysDataForQueueChart(30);
    };

    const getNinetyDaysDataForQueueChart = () => {
        setQueueDurationData("ninety_days");
        getDaysDataForQueueChart(90);
    };

    const getWeeklyData = () => {
        setSignupsChartPeriod("week");
    };

    const getMonthlyData = () => {
        setSignupsChartPeriod("month");
        getMonthlyDataForChart();
    };

    const getYearlyData = () => {
        setSignupsChartPeriod("year");
        getYearlyDataForChart();
    };

    function formatTimestamp(timestamp) {
        return moment.unix(timestamp).format("MMMM D, HH:mm");
    }

    const onClickHandlers = {
        getSevenDaysDataForQueueChart,
        getThirtyDaysDataForQueueChart,
        getNinetyDaysDataForQueueChart,
        getSevenDaysDataForMatchmakingChart,
        getThirtyDaysDataForMatchmakingChart,
        getNinetyDaysDataForMatchmakingChart,
        getSevenDaysDataForMatchmakingCapacityChart,
        getThirtyDaysDataForMatchmakingCapacityChart,
        getNinetyDaysDataForMatchmakingCapacityChart,
        getWeeklyData,
        getMonthlyData,
        getYearlyData
    };

    return (
        <div className={classes.root}>
            <div className={classes.headerRow}>
                <Typography className={classes.sectionTitle}>System Monitoring</Typography>
                <Button
                    component={Link}
                    to="/dashboard"
                    variant="contained"
                    color="primary"
                    startIcon={<DnsIcon />}
                >
                    View Servers Dashboard
                </Button>
            </div>
            <div className={classes.containerSystemHealthAnalytics}>
                <Typography className={classes.title}>System Health</Typography>
                <div className={classes.containerCards} style={{ marginTop: "1rem" }}>
                    {systemHealthCardsData.map((card) => (
                        <StatCard 
                            key={card.id} 
                            cardData={card}
                            serversAliveCount={serversAliveCount}
                        />
                    ))}
                </div>
            </div>
            <div className={classes.containerQueueAnalitycs}>
                <Typography className={classes.title}>Queue Analitycs</Typography>
                <div className={classes.containerCards} style={{ marginTop: "1rem" }}>
                    {queueCardsData.map((card) => (
                        <StatCard 
                            key={card.id} 
                            cardData={card} 
                            queueAnalitycsSummary={queueAnalitycsSummary}
                            serversAliveCount={serversAliveCount}
                        />
                    ))}
                </div>
                <div className={classes.containerChartLayout} style={{ gridTemplateColumns: "1fr 30rem" }}>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Queue Duration</Typography>
                            <div className={classes.containerSelectLabels}>
                                <PeriodLabelsSelectorChart 
                                    chartType="queueDuration" 
                                    labelsText={["7 Days", "30 Days", "90 Days"]}
                                    queueDurationData={queueDurationData}
                                    onClickHandlers={onClickHandlers}
                                />
                            </div>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[0].ref}></canvas> 
                        </div>
                    </div>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Players Queued Per Day</Typography>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[1].ref}></canvas>
                        </div>
                    </div>
                </div>
                <div className={classes.containerQueueBarChartsLayout}>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Queue Outcome Distribution</Typography>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[2].ref}></canvas>
                        </div>
                    </div>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Queue Completion Rate Per Day</Typography>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[3].ref}></canvas>
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.containerMMOverview}>
                <Typography className={classes.title}>Matchmaking Activity</Typography>
                <div className={classes.containerCards} style={{ marginTop: "0.5rem" }}>
                    {mmActivityStatCardsData.map((card) => (
                        <StatCard 
                            key={card.id} 
                            cardData={card} 
                            mmActivitySummary={mmActivitySummary}
                            queueAnalitycsSummary={queueAnalitycsSummary}
                            serversAliveCount={serversAliveCount}
                        />
                    ))}
                </div>
                <div className={classes.containerChartLayout}>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Matchmaking Activity Over Time</Typography>
                            <div className={classes.containerSelectLabels}>
                                <PeriodLabelsSelectorChart 
                                    chartType="matchmakingActivity" 
                                    labelsText={["7 Days", "30 Days", "90 Days"]}
                                    matchesChartPeriod={matchesChartPeriod}
                                    onClickHandlers={onClickHandlers}
                                />
                            </div>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[4].ref}></canvas> 
                        </div>
                    </div>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}> 
                            <Typography className={classes.title}>Matchduration Distribution</Typography>
                        </div>
                        <div className={`${classes.chartCanvasContainer} ${classes.doughnutContainer}`}>
                            <canvas ref={monitoringCharts[5].ref}></canvas> 
                        </div>
                    </div>
                </div>
                <div className={classes.containerChart} style={{ marginTop: "2rem" }}>
                    <div className={classes.containerChartTitle}>
                        <Typography className={classes.title}>Capacity Pressure Over Time</Typography>
                        <div className={classes.containerSelectLabels}>
                            <PeriodLabelsSelectorChart
                                chartType="matchmakingCapacity"
                                labelsText={["7 Days", "30 Days", "90 Days"]}
                                matchmakingCapacityPeriod={matchmakingCapacityPeriod}
                                onClickHandlers={onClickHandlers}
                            />
                        </div>
                    </div>
                    <div className={classes.chartCanvasContainer}>
                        <canvas ref={monitoringCharts[7].ref}></canvas>
                    </div>
                </div>
            </div>
            <div className={classes.containerSignupAnalitycs}>
                <Typography className={classes.title}>Signup Analitycs</Typography>
                <div className={classes.containerChartLayout} style={{ marginTop: "1rem" }}>
                    <div className={classes.containerChart}>
                        <div className={classes.containerChartTitle}>
                            <Typography className={classes.title}>Ouro Signup Activity</Typography>
                            <div className={classes.containerSelectLabels}>
                                <PeriodLabelsSelectorChart 
                                    chartType="signupActivity" 
                                    labelsText={["Week", "Month", "Year"]}
                                    signupsChartPeriod={signupsChartPeriod}
                                    onClickHandlers={onClickHandlers}
                                />
                            </div>
                        </div>
                        <div className={classes.chartCanvasContainer}>
                            <canvas ref={monitoringCharts[6].ref}></canvas> 
                        </div>
                    </div>
                    <div className={classes.containerNewPlayers}>
                        <Typography className={classes.title}>Newest Players</Typography>
                        <div className={classes.containerNewPlayersList}>
                            {newestPlayers.map((player) => (
                                <div className={classes.newPlayerItem} key={player.userId}>
                                    <img src={player.picture} alt={player.nickname} className={classes.newPlayerPicture} />
                                    <div className={classes.newPlayerInfoContainer}>
                                        <Typography className={classes.newPlayerNickname}>{player.nickname}</Typography>
                                        <img src={player.flagImageUrl} alt={player.country} className={classes.newPlayerCountryFlag} /> 
                                    </div>
                                    <div className={classes.newPlayerJoinedDateContainer}>
                                        <Typography className={classes.newPlayerJoinedDate}>{formatTimestamp(player.joined)}</Typography>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            
        </div>
    );
}